import witcherStrings from '../witcher/strings.en.json';
import witcherManifest from '../witcher/theme.json';
import { deepMerge } from './merge.js';
import { type Skin, type Strings, StringsSchema, type ThemeManifest, ThemeManifestSchema } from './schema.js';

export { deepMerge } from './merge.js';
export * from './pack.js';
export * from './schema.js';

export const WITCHER_THEME: ThemeManifest = ThemeManifestSchema.parse(witcherManifest);
export const WITCHER_STRINGS: Strings = StringsSchema.parse(witcherStrings);

export const BUILT_IN_THEMES: Readonly<Record<string, { manifest: ThemeManifest; strings: Strings }>> = {
  witcher: { manifest: WITCHER_THEME, strings: WITCHER_STRINGS },
};

export interface ResolvedTheme {
  manifest: ThemeManifest;
  strings: Strings;
  /** Ordered list of layer ids that produced this theme (theme first, then skins). */
  layers: string[];
}

/** Apply skins on top of a theme. Later skins win. The result is re-validated. */
export function resolveTheme(
  theme: { manifest: ThemeManifest; strings: Strings },
  skins: Skin[] = [],
): ResolvedTheme {
  const manifest = ThemeManifestSchema.parse(
    deepMerge(
      theme.manifest,
      ...skins.map((s) => ({ colors: s.colors, fonts: s.fonts, vfx: s.vfx, assets: s.assets })),
    ),
  );
  const strings = StringsSchema.parse(deepMerge(theme.strings, ...skins.map((s) => s.strings)));
  return { manifest, strings, layers: [theme.manifest.id, ...skins.map((s) => s.id)] };
}

/** Display name for a card id; falls back to a readable version of the id so nothing renders blank. */
export function cardName(strings: Strings, cardId: string): string {
  return strings.cards[cardId]?.name ?? cardId.replace(/_/g, ' ');
}

/**
 * Maps a theme-relative asset key to a URL. Built-in packs live under `/themes/<id>/`; user packs
 * register their own resolver (blob URLs from IndexedDB, or a remote base URL).
 */
export type AssetResolver = (key: string) => string | undefined;

export function builtInResolver(themeId: string, base = '/themes'): AssetResolver {
  return (key) => `${base}/${themeId}/${key}`;
}

/** First resolver that returns a URL wins; skins registered later go first. */
export function chainResolvers(...resolvers: AssetResolver[]): AssetResolver {
  return (key) => {
    for (const r of resolvers) {
      const url = r(key);
      if (url) return url;
    }
    return undefined;
  };
}
